import React, { useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const REASONS = [
  { key: 'spam', labelKey: 'reasonSpam' },
  { key: 'harassment', labelKey: 'reasonHarassment' },
  { key: 'nudity', labelKey: 'reasonNudity' },
  { key: 'hate', labelKey: 'reasonHate' },
  { key: 'misinformation', labelKey: 'reasonMisinformation' },
  { key: 'other', labelKey: 'reasonOther' },
];

const TITLE_KEY_BY_TYPE = {
  user: 'reportUser',
  post: 'reportPost',
  comment: 'reportComment',
  message: 'reportMessage',
};

const ReportModal = ({ open, onClose, targetType, targetId, token, API, t, isRTL }) => {
  const [reason, setReason] = useState('spam');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {ok, msg}

  if (!open) return null;
  const titleKey = TITLE_KEY_BY_TYPE[targetType] || 'report';

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    setDone(null);
    try {
      const res = await axios.post(
        `${API}/reports`,
        {
          target_type: targetType,
          target_id: targetId,
          reason,
          details: details ? details.slice(0, 500) : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDone({
        ok: true,
        msg: res.data?.duplicate ? t('reportDuplicate') : t('reportSubmitted'),
      });
      // Auto-close after the user has time to read the success
      setTimeout(() => {
        onClose && onClose();
      }, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setDone({ ok: false, msg: typeof detail === 'string' ? detail : t('error') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="report-modal"
      dir={isRTL ? 'rtl' : 'ltr'}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
    >
      {/* Backdrop (only closes when not showing success) */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (!done?.ok) onClose && onClose();
        }}
      />
      <div
        className="relative bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-base font-bold">{t(titleKey)}</h3>
          <button
            onClick={onClose}
            data-testid="report-close"
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
            aria-label={t('closeModal')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3 overflow-y-auto">
          <p className="text-xs font-medium text-gray-600">{t('reportReason')}</p>
          <ul className="space-y-1.5">
            {REASONS.map((r) => (
              <li key={r.key}>
                <label
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer ${
                    reason === r.key
                      ? 'border-purple-400 bg-purple-50 text-purple-700'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={r.key}
                    checked={reason === r.key}
                    onChange={() => setReason(r.key)}
                    data-testid={`report-reason-${r.key}`}
                    className="accent-purple-600"
                  />
                  <span className="text-sm">{t(r.labelKey)}</span>
                </label>
              </li>
            ))}
          </ul>

          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">{t('reportDetails')}</p>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              maxLength={500}
              data-testid="report-details"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400"
            />
            <p className="text-[11px] text-gray-400 mt-0.5 text-end">{details.length}/500</p>
          </div>

          {done && (
            <p
              data-testid="report-result"
              className={`text-sm ${done.ok ? 'text-emerald-600' : 'text-red-600'}`}
            >
              {done.msg}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={submitting || (done && done.ok)}
              data-testid="report-submit"
              className="flex-1 py-2.5 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-50"
            >
              {submitting ? t('reportSubmitting') : t('reportSubmit')}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="report-cancel"
              className="py-2.5 px-5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              {t('cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportModal;
